import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArtButton, useApiService, useIdpService } from "@art/shared-ui";
import { useSelector } from "react-redux";
import { useOutletContext, useParams } from "react-router-dom";
import { RootState } from "../../../../store";
import { ResourceScope } from "../../application-users/resource-access";
import type { RoleDetailContext } from "./index";

type Option = { label: string; value: string };

const normalize = (values: Option[]) =>
  values.some((item) => item.value === "*")
    ? [values.find((item) => item.value === "*")!]
    : values;

export default function RoleResourceAccess() {
  const { role, canManageRole } = useOutletContext<RoleDetailContext>();
  const { id } = useParams<{ id: string }>();
  const tenantName = useSelector((state: RootState) => state.auth.tenantName);
  const apiService = useApiService();
  const idpService = useIdpService();
  const [projects, setProjects] = useState<Option[]>([]);
  const [environments, setEnvironments] = useState<Option[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<Option[]>([]);
  const [selectedEnvironments, setSelectedEnvironments] = useState<Option[]>(
    [],
  );
  const [scopes, setScopes] = useState<ResourceScope[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const endpoint = useMemo(
    () => `api/tenants/${tenantName}/roles/${id}/resource-access`,
    [tenantName, id],
  );

  useEffect(() => {
    if (!tenantName || !id) return;
    setLoading(true);
    setLoadError(false);
    Promise.all([
      apiService.get("/resource-access-catalog/projects"),
      apiService.get("/resource-access-catalog/environments?limit=100"),
      idpService.get(endpoint),
    ])
      .then(([projectResponse, environmentResponse, scopeResponse]: any[]) => {
        setProjects([
          { label: "All projects", value: "*" },
          ...(projectResponse.data?.data?.projects || []).map((item: any) => ({
            label: item.title,
            value: item.project_key,
          })),
        ]);
        setEnvironments([
          { label: "All environments", value: "*" },
          ...(environmentResponse.data?.data?.environments || []).map(
            (item: any) => ({ label: item.title, value: item.title }),
          ),
        ]);
        setScopes(
          Array.isArray(scopeResponse.data?.data)
            ? scopeResponse.data.data
            : [],
        );
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [apiService, endpoint, id, idpService, tenantName]);

  const labelFor = (scope: ResourceScope) => {
    const project =
      projects.find((item) => item.value === scope.project_key)?.label ||
      scope.project_key;
    const environment =
      environments.find((item) => item.value === scope.environment_name)
        ?.label || scope.environment_name;
    return `${project} · ${environment}`;
  };

  const save = async () => {
    if (!canManageRole || loadError) return;
    setSaving(true);
    try {
      const additions = normalize(selectedProjects).flatMap((project) =>
        normalize(selectedEnvironments).map((environment) => ({
          project_key: project.value,
          environment_name: environment.value,
        })),
      );
      const next = [...scopes, ...additions].filter(
        (scope, index, all) =>
          all.findIndex(
            (item) =>
              item.project_key === scope.project_key &&
              item.environment_name === scope.environment_name,
          ) === index,
      );
      const effective = next.some(
        (scope) => scope.project_key === "*" && scope.environment_name === "*",
      )
        ? next.filter(
            (scope) =>
              scope.project_key === "*" && scope.environment_name === "*",
          )
        : next;
      await idpService.put(endpoint, { resource_scopes: effective });
      setScopes(effective);
      setSelectedProjects([]);
      setSelectedEnvironments([]);
      idpService.showSnack(
        effective.length
          ? "Role resource access updated"
          : "Role resource access revoked",
        "success",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );

  return (
    <Stack spacing={3} sx={{ maxWidth: 900 }}>
      <Box>
        <Typography variant="h6">Project and environment access</Typography>
        <Typography color="text.secondary">
          These grants are inherited by every user assigned to this role.
          Removing a grant here revokes that inherited path, while any direct
          user grant remains independent.
        </Typography>
      </Box>
      {loadError && (
        <Alert severity="error">
          Resource access could not be loaded. Refresh before making changes.
        </Alert>
      )}
      {role?.is_default && (
        <Alert severity="info">
          This default role is system-defined. Its resource access can be
          reviewed but not changed from the console.
        </Alert>
      )}
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {scopes.length ? (
          scopes.map((scope) => (
            <Chip
              key={`${scope.project_key}:${scope.environment_name}`}
              label={labelFor(scope)}
              onDelete={
                canManageRole
                  ? () =>
                      setScopes((current) =>
                        current.filter(
                          (item) =>
                            item.project_key !== scope.project_key ||
                            item.environment_name !== scope.environment_name,
                        ),
                      )
                  : undefined
              }
            />
          ))
        ) : (
          <Typography color="text.secondary">
            No project or environment grants
          </Typography>
        )}
      </Stack>
      <Autocomplete
        multiple
        disabled={!canManageRole || loadError}
        options={projects}
        value={selectedProjects}
        onChange={(_, value) => setSelectedProjects(value)}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        renderInput={(params) => <TextField {...params} label="Add projects" />}
      />
      <Autocomplete
        multiple
        disabled={!canManageRole || loadError}
        options={environments}
        value={selectedEnvironments}
        onChange={(_, value) => setSelectedEnvironments(value)}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        renderInput={(params) => (
          <TextField {...params} label="Add environments" />
        )}
      />
      <Box>
        <ArtButton
          buttonType="button"
          dataQa="save-role-resource-access"
          label={saving ? "Saving…" : "Save access"}
          onClick={save}
          disabled={saving || !canManageRole || loadError}
        />
      </Box>
    </Stack>
  );
}
